import { useEffect, useState } from "react";

const DEFAULT_HEIGHT = 800;

function readViewportHeight(): number {
  if (typeof window === "undefined") return DEFAULT_HEIGHT;
  return window.visualViewport?.height ?? window.innerHeight;
}

/**
 * Tracks the visible viewport height, shrinking when the OS keyboard appears.
 * Subscribes to visualViewport resize/scroll plus a window resize fallback.
 * SSR-safe: returns a sensible default when window is unavailable.
 */
export function useVisualViewportHeight(): number {
  const [height, setHeight] = useState<number>(readViewportHeight);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => setHeight(readViewportHeight());
    update();

    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    return () => {
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return height;
}
